import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { Button, Input } from '@treezor/design-system';

import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useConfirmationDialog } from '@/hooks/useConfirmationModal';
import { useInputs } from '@/hooks/useInputs';

import { setBulkCardCreationStep } from '@/store/actions/bulkCardCreations.actions';

interface ICardQuantityFormInputs {
  cards: number;
}

export const CardsQuantity = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const activeStep = useAppSelector((state) => state.bulkCardCreation.currentStep);
  const bulkCardCreationDraft = useAppSelector((state) => state.bulkCardCreation.bulkCardCreationDraft);

  const defaultValues: ICardQuantityFormInputs = {
    cards: bulkCardCreationDraft && bulkCardCreationDraft.number ? bulkCardCreationDraft.number : 2,
  };

  const formMethods = useForm<ICardQuantityFormInputs>({ defaultValues });

  const { handleSubmit, watch } = formMethods;

  const { handleCardsChange } = useInputs(
    [
      {
        name: 'cards',
        type: 'text',
        options: {
          required: true,
          validate: {
            moreThan: (cards: number) => {
              return cards > 0 || t('pages.administration.bulkCards.cardsQuantity.forms.rules.validate');
            },
          },
        },
      },
    ],
    formMethods,
  );

  const cardsQuantity = watch('cards');

  const toggleConfirmation = () => ConfirmationCardsQuantity.toggle();

  const onSubmit = (data: ICardQuantityFormInputs) => {
    if (data && data?.cards < 1000) dispatch(setBulkCardCreationStep(activeStep + 1, { number: data.cards }));
    else toggleConfirmation();
  };

  const manageConfirmation = () => {
    dispatch(setBulkCardCreationStep(activeStep + 1, { number: cardsQuantity }));
    toggleConfirmation();
  };

  const ConfirmationCardsQuantity = useConfirmationDialog({
    title: t('pages.administration.bulkCards.cardsQuantity.modal.title'),
    description: t('pages.administration.bulkCards.cardsQuantity.modal.description', { nbCards: cardsQuantity }),
    confirm: { label: t('pages.modal.sure'), color: 'danger', action: manageConfirmation },
  });

  return (
    <>
      <h5 className="typo-h5-bold text-secondary">{t('pages.administration.bulkCards.cardsQuantity.title')}</h5>
      <form className="flex flex-col gap-6 mt-7 w-[250px]" onSubmit={handleSubmit(onSubmit)}>
        <Input
          label={t('pages.administration.bulkCards.cardsQuantity.forms.nbCards')}
          name="cards"
          type="number"
          required
          onChange={handleCardsChange}
          error={cardsQuantity > 0 ? undefined : t('pages.administration.bulkCards.cardsQuantity.forms.rules.validate')}
          defaultValue={2}
        />
        <div className="flex items-center justify-center">
          <Button id="next-bulk-cards-quantity-btn" type="submit">
            {t('pages.administration.bulkCards.next')}
          </Button>
        </div>
      </form>

      <ConfirmationCardsQuantity.ConfirmationDialog />
    </>
  );
};
